import type { ReactNode } from "react";
import { Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverTip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { cn } from "@/lib/utils";
import { GUIDES, type GuideId } from "@/v2/stage-vocabulary";

/**
 * The way to the page's walkthrough video. The videos are not made yet, so
 * a press does nothing; the tip says so, and the control stays live so that
 * it reads as one waiting for its video rather than one that is broken.
 */
export function StageGuide({ guide }: { guide: GuideId }) {
  const { lead, topic, trail } = GUIDES[guide];
  return (
    <HoverTip
      tip="The walkthrough video is on its way."
      wide={false}
      align="end"
    >
      {({ triggerProps }) => (
        <Button type="button" variant="guide" {...triggerProps}>
          <Video aria-hidden />
          <span>
            {lead}
            <i>{topic}</i>
            {trail}
          </span>
        </Button>
      )}
    </HoverTip>
  );
}

/** The lede's two measures: the page's, and the Thesis pages' wider one. */
const LEDE = {
  page: "mt-[9px] max-w-[600px] text-sm leading-[1.55] text-muted-foreground",
  thesis:
    "mt-[9px] max-w-[680px] text-[13.5px] leading-[1.55] text-muted-foreground",
} as const;

type LedeMeasure = keyof typeof LEDE;

/** The title with the controls beside it, and the lede under them. */
function StageHead({
  title,
  lede,
  actions,
  guide,
  centred = false,
  measure = "page",
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  guide?: GuideId;
  centred?: boolean;
  measure?: LedeMeasure;
}) {
  return (
    <>
      <div
        className={cn(
          "flex items-start gap-4",
          centred ? "justify-center" : "justify-between",
        )}
      >
        <h1 className="text-[26px] leading-[1.12] font-extrabold tracking-[-0.03em]">
          {title}
        </h1>
        {(actions !== undefined || guide !== undefined) && (
          <div className="flex flex-none items-center gap-2 pt-[3px]">
            {actions}
            {guide !== undefined && <StageGuide guide={guide} />}
          </div>
        )}
      </div>
      {lede !== undefined && (
        <p className={cn(LEDE[measure], centred && "mx-auto")}>{lede}</p>
      )}
    </>
  );
}

/**
 * The head of a page of the workflow: where it sits, what it is, and what it
 * is for, in the three lines every stage opens with.
 *
 * The lede holds the design's measure. Eight pages chose six between them, two
 * of them by choosing none at all, which on the wide column ran a paragraph to
 * about a hundred and thirty characters a line. v0.28 sets the Thesis pages,
 * Invention and the three Novelty tabs, a step wider and a half point smaller,
 * so they ask for `thesis`.
 *
 * The page holds one column too, centred at the design's 1000px, so no screen
 * sets a width of its own. Figures takes the design's wider 1040.
 */
export function StagePage({
  eyebrow,
  title,
  lede,
  actions,
  guide,
  back,
  mark,
  taskId,
  testId,
  align = "start",
  width = "page",
  measure = "page",
  children,
}: {
  /** `center` for a page that stands on its own, as a long wait does: the mark
   * over the title, and the head centred in the narrower measure. */
  align?: "start" | "center";
  /** `wide` for the figures pages, which the design sets wider than the rest. */
  width?: "page" | "wide";
  /** `thesis` for Invention and the Novelty tabs, whose lede the design sets wider. */
  measure?: LedeMeasure;
  /** The way to the page above, drawn over the title, for a page reached by opening something. */
  back?: ReactNode;
  /** A picture of what the page is about, drawn before the title and its lede: a client's logo. */
  mark?: ReactNode;
  /** Where in the workflow this page sits, as the rail names it. Absent on a
   * page that sits in no stage. */
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  /** Controls that belong beside the title rather than under it. */
  actions?: ReactNode;
  /** The page's walkthrough, drawn last beside the title. */
  guide?: GuideId;
  /** The draft on screen. Read by the tests that check a page is showing one. */
  taskId?: string;
  testId?: string;
  children?: ReactNode;
}) {
  const head = { title, lede, actions, guide, measure };
  return (
    <section
      data-testid={testId}
      data-task-id={taskId}
      className={cn(
        "mx-auto w-full pb-10 pt-6",
        width === "wide" ? "max-w-[1040px]" : "max-w-[1000px]",
        align === "center" && "max-w-[900px] text-center",
      )}
    >
      {/* The design hides the eyebrow on every screen; a reader still hears where the page sits. */}
      {eyebrow !== undefined && (
        <MicroLabel as="p" className="sr-only">
          {eyebrow}
        </MicroLabel>
      )}
      {back}
      {align === "center" ? (
        <>
          {mark !== undefined && (
            <div className="mb-5 flex justify-center">{mark}</div>
          )}
          <StageHead {...head} centred />
        </>
      ) : mark === undefined ? (
        <StageHead {...head} />
      ) : (
        <div className="flex items-center gap-6">
          {mark}
          <div className="min-w-0 flex-1">
            <StageHead {...head} />
          </div>
        </div>
      )}
      {children}
    </section>
  );
}
